class AquiromaHeroCarousel extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));
    this.status = this.querySelector('[data-carousel-status]');
    this.currentIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    this.autoplay = this.dataset.autoplay === 'true';
    this.interval = Math.max(Number.parseInt(this.dataset.interval, 10) || 5000, 3000);
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.pointerPaused = false;
    this.focusPaused = false;
    this.editorPaused = false;

    this.previousButton = this.querySelector('[data-carousel-previous]');
    this.nextButton = this.querySelector('[data-carousel-next]');

    this.handlePrevious = () => this.show(this.currentIndex - 1, true);
    this.handleNext = () => this.show(this.currentIndex + 1, true);
    this.handleMouseEnter = () => {
      this.pointerPaused = true;
      this.stop();
    };
    this.handleMouseLeave = () => {
      this.pointerPaused = false;
      this.start();
    };
    this.handleFocusIn = () => {
      this.focusPaused = true;
      this.stop();
    };
    this.handleFocusOut = (event) => {
      if (!this.contains(event.relatedTarget)) {
        this.focusPaused = false;
        this.start();
      }
    };
    this.handleVisibility = () => {
      if (document.hidden) this.stop();
      else this.start();
    };
    this.handleMotionChange = () => {
      this.stop();
      this.start();
    };
    this.handleBlockSelect = (event) => {
      const slide = event.target.closest('[data-carousel-slide]');
      if (!slide || !this.contains(slide)) return;

      const index = this.slides.indexOf(slide);
      if (index >= 0) this.show(index, false);
      this.editorPaused = true;
      this.stop();
    };
    this.handleBlockDeselect = (event) => {
      if (!this.contains(event.target)) return;
      this.editorPaused = false;
      this.start();
    };

    this.previousButton?.addEventListener('click', this.handlePrevious);
    this.nextButton?.addEventListener('click', this.handleNext);
    this.dotHandlers = this.dots.map((dot) => {
      const handler = () => this.show(Number.parseInt(dot.dataset.carouselDot, 10), true);
      dot.addEventListener('click', handler);
      return { dot, handler };
    });
    this.addEventListener('mouseenter', this.handleMouseEnter);
    this.addEventListener('mouseleave', this.handleMouseLeave);
    this.addEventListener('focusin', this.handleFocusIn);
    this.addEventListener('focusout', this.handleFocusOut);
    document.addEventListener('visibilitychange', this.handleVisibility);
    document.addEventListener('shopify:block:select', this.handleBlockSelect);
    document.addEventListener('shopify:block:deselect', this.handleBlockDeselect);
    this.reducedMotion.addEventListener?.('change', this.handleMotionChange);

    this.show(this.currentIndex, false);
    this.start();
  }

  disconnectedCallback() {
    this.stop();
    this.previousButton?.removeEventListener('click', this.handlePrevious);
    this.nextButton?.removeEventListener('click', this.handleNext);
    this.dotHandlers?.forEach(({ dot, handler }) => dot.removeEventListener('click', handler));
    this.removeEventListener('mouseenter', this.handleMouseEnter);
    this.removeEventListener('mouseleave', this.handleMouseLeave);
    this.removeEventListener('focusin', this.handleFocusIn);
    this.removeEventListener('focusout', this.handleFocusOut);
    document.removeEventListener('visibilitychange', this.handleVisibility);
    document.removeEventListener('shopify:block:select', this.handleBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);
    this.reducedMotion?.removeEventListener?.('change', this.handleMotionChange);
    this.initialized = false;
  }

  show(requestedIndex, announce) {
    if (this.slides.length === 0) return;

    const index = (requestedIndex + this.slides.length) % this.slides.length;
    this.currentIndex = index;

    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      slide.toggleAttribute('inert', !active);
    });

    this.dots.forEach((dot, dotIndex) => {
      const active = dotIndex === index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });

    if (announce && this.status) {
      this.status.textContent = `Message ${index + 1} of ${this.slides.length}`;
    }

    if (announce) {
      this.stop();
      this.start();
    }
  }

  start() {
    if (
      !this.autoplay ||
      this.slides.length < 2 ||
      this.reducedMotion.matches ||
      this.editorPaused ||
      this.pointerPaused ||
      this.focusPaused ||
      document.hidden
    ) return;

    this.stop();
    this.timer = window.setInterval(() => this.show(this.currentIndex + 1, false), this.interval);
  }

  stop() {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = undefined;
  }
}

if (!customElements.get('aq-hero-carousel')) {
  customElements.define('aq-hero-carousel', AquiromaHeroCarousel);
}
